import { getAddress, isAddressEqual, zeroAddress } from 'viem';
import type { Address } from 'viem';
import { readContract } from 'wagmi/actions';

import {
    ANCHOR_CHAIN_IDS,
    anchorLabel,
    DEFAULT_ANCHOR_CHAIN_ID,
    walletWagmiConfig,
    type AnchorChainId,
} from '@/features/wallet/config';
import {
    custodialManagerFor,
    HYPER_EVM_CUSTODIAL_WALLET_MANAGER,
} from '@/lib/config/custodialManagers';
import { hasErrorCode } from '@/lib/errors';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';

// re-exported for the existing consumers that import it from this module
export { HYPER_EVM_CUSTODIAL_WALLET_MANAGER };

const managerAbi = [
    {
        name: 'wallets',
        type: 'function',
        stateMutability: 'view',
        inputs: [{ name: 'owner', type: 'address' }],
        outputs: [{ type: 'address' }],
    },
] as const;

export interface TradingSessionIdentity {
    owner: Address;
    authorizationWallet: Address;
    tradingWallet: Address;
}

/** `wallets(owner)` on the anchor's CustodialWalletManager. Zero when unset. */
export async function readAuthorizationWallet(
    owner: Address,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Promise<Address> {
    return readContract(walletWagmiConfig, {
        address: custodialManagerFor(anchorChainId),
        abi: managerAbi,
        functionName: 'wallets',
        args: [owner],
        chainId: anchorChainId,
    });
}

/** `wallets(owner)` on the HyperEVM CustodialWalletManager. Zero when unset. */
export async function readTradingWallet(owner: Address): Promise<Address> {
    return hyperEvmPublicClient.readContract({
        address: HYPER_EVM_CUSTODIAL_WALLET_MANAGER,
        abi: managerAbi,
        functionName: 'wallets',
        args: [owner],
    });
}

export interface SessionAnchorDependencies {
    getAuthorizationWallet: (
        owner: Address,
        anchorChainId: AnchorChainId,
    ) => Promise<Address>;
    getTradingWallet: (owner: Address) => Promise<Address>;
}

const defaultSessionAnchorDependencies: SessionAnchorDependencies = {
    getAuthorizationWallet: async (owner, anchorChainId) =>
        readAuthorizationWallet(owner, anchorChainId),
    getTradingWallet: readTradingWallet,
};

export class TradingSessionWalletMissingError extends Error {
    constructor(
        readonly code: 'authorizationWalletMissing' | 'tradingWalletMissing',
        readonly anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
    ) {
        super(
            code === 'authorizationWalletMissing'
                ? `Set up the owner’s authorization wallet on ${anchorLabel(anchorChainId)} before opening a trading window.`
                : 'Fund the owner account once to set up its HyperEVM trading wallet.',
        );
        this.name = 'TradingSessionWalletMissingError';
    }
}

export function isTradingSessionWalletMissingError(
    error: unknown,
): error is TradingSessionWalletMissingError {
    return (
        error instanceof TradingSessionWalletMissingError ||
        hasErrorCode(
            error,
            'authorizationWalletMissing',
            'tradingWalletMissing',
        )
    );
}

/**
 * The owner's authorization wallet on WHICHEVER anchor holds one, or the zero
 * address when neither does. An unreadable chain is not proof of a missing
 * account, so a read failure only surfaces when no anchor produced a wallet.
 */
export async function readAuthorizationWalletOnAnyAnchor(
    ownerInput: Address,
    dependencies: SessionAnchorDependencies = defaultSessionAnchorDependencies,
): Promise<Address> {
    const owner = getAddress(ownerInput);
    const reads = await Promise.allSettled(
        ANCHOR_CHAIN_IDS.map(async (anchorChainId) =>
            dependencies.getAuthorizationWallet(owner, anchorChainId),
        ),
    );
    let failure: unknown = null;
    for (const read of reads) {
        if (read.status === 'rejected') {
            failure = failure ?? read.reason;
            continue;
        }
        const wallet = getAddress(read.value);
        if (!isAddressEqual(wallet, zeroAddress)) return wallet;
    }
    if (failure !== null) throw failure;
    return zeroAddress;
}

/** How the session anchor was picked for an owner. */
export interface SessionAnchorResolution {
    anchorChainId: AnchorChainId;
    identity: TradingSessionIdentity;
    /**
     * The other anchor that ALSO holds an authorization wallet for this owner,
     * or null. A passkey lives on exactly one anchor's wallet, so a ceremony
     * that guessed wrong retries here.
     */
    alternate: {
        anchorChainId: AnchorChainId;
        identity: TradingSessionIdentity;
    } | null;
}

/**
 * Pick the anchor a session signs under from what the owner actually HAS,
 * instead of asking: both anchors' CustodialWalletManagers are read in
 * parallel, an anchor holding a wallet for this owner is a candidate, and the
 * connected wallet's chain breaks a tie (else the declared anchor order,
 * Cronos first). The trading wallet is the same HyperEVM wallet whichever
 * anchor signs, so the pick never changes which account the session reaches.
 *
 * Only an owner with a wallet on NEITHER anchor is a missing account; an
 * unreadable chain is not proof of one, so a read failure only surfaces when
 * no candidate exists at all.
 */
export async function resolveSessionAnchor(
    ownerInput: string,
    preferredChainId?: number | null,
    dependencies: SessionAnchorDependencies = defaultSessionAnchorDependencies,
): Promise<SessionAnchorResolution> {
    const owner = getAddress(ownerInput);
    const [anchorReads, tradingWalletInput] = await Promise.all([
        Promise.allSettled(
            ANCHOR_CHAIN_IDS.map(async (anchorChainId) =>
                dependencies.getAuthorizationWallet(owner, anchorChainId),
            ),
        ),
        dependencies.getTradingWallet(owner),
    ]);

    let readFailure: unknown = null;
    const anchors: {
        anchorChainId: AnchorChainId;
        authorizationWallet: Address;
    }[] = [];
    ANCHOR_CHAIN_IDS.forEach((anchorChainId, index) => {
        const read = anchorReads[index]!;
        if (read.status === 'rejected') {
            readFailure = readFailure ?? read.reason;
            return;
        }
        const authorizationWallet = getAddress(read.value);
        if (isAddressEqual(authorizationWallet, zeroAddress)) return;
        anchors.push({ anchorChainId, authorizationWallet });
    });

    if (anchors.length === 0) {
        if (readFailure !== null) throw readFailure;
        throw new TradingSessionWalletMissingError('authorizationWalletMissing');
    }

    const tradingWallet = getAddress(tradingWalletInput);
    if (isAddressEqual(tradingWallet, zeroAddress)) {
        throw new TradingSessionWalletMissingError('tradingWalletMissing');
    }

    const toCandidate = (anchor: (typeof anchors)[number]) => ({
        anchorChainId: anchor.anchorChainId,
        identity: {
            owner,
            authorizationWallet: anchor.authorizationWallet,
            tradingWallet,
        },
    });
    const preferred =
        anchors.find(
            (anchor) => anchor.anchorChainId === Number(preferredChainId),
        ) ?? anchors[0]!;
    const alternate = anchors.find(
        (anchor) => anchor.anchorChainId !== preferred.anchorChainId,
    );
    return {
        ...toCandidate(preferred),
        alternate: alternate ? toCandidate(alternate) : null,
    };
}

export async function resolveTradingSessionIdentity(
    ownerInput: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
    dependencies: SessionAnchorDependencies = defaultSessionAnchorDependencies,
): Promise<TradingSessionIdentity> {
    const owner = getAddress(ownerInput);
    const [authorizationWalletInput, tradingWalletInput] = await Promise.all([
        dependencies.getAuthorizationWallet(owner, anchorChainId),
        dependencies.getTradingWallet(owner),
    ]);
    const authorizationWallet = getAddress(authorizationWalletInput);
    const tradingWallet = getAddress(tradingWalletInput);
    if (isAddressEqual(authorizationWallet, zeroAddress)) {
        throw new TradingSessionWalletMissingError(
            'authorizationWalletMissing',
            anchorChainId,
        );
    }
    if (isAddressEqual(tradingWallet, zeroAddress)) {
        throw new TradingSessionWalletMissingError('tradingWalletMissing');
    }
    return { owner, authorizationWallet, tradingWallet };
}
